import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, MessageSquare, X, Send, Users, User, GraduationCap, Zap } from 'lucide-react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import './AgoraService.css';
import { 
  initSocket, 
  joinRoom, 
  leaveRoom, 
  toggleMuteSocket, 
  toggleVideoSocket, 
  disconnectSocket, 
  sendChatMessage,
  onChatMessage,
  onCallEnded
} from './services/socketService';
import { DeviceService } from './services/deviceService';
import { MENTORZAA_API_URL, SERVER_URL } from './config';



import ProviderFrameMeeting from './components/providers/ProviderFrameMeeting';
import MentorzaLogo from './components/brand/MentorzaLogo';
import {
  bootstrapMeetingRoom,
  completeMeetingRoom,
  fetchMeetingRoomStatus,
  fetchProviderJoinConfig,
  sendMeetingEvent
} from './services/meetingRouterService';

// Clean channel name
const cleanChannelName = (rawChannel) => {
  if (!rawChannel) return '';
  let cleaned = String(rawChannel).trim();
  if (cleaned.includes('http') || cleaned.includes('://') || cleaned.includes('localhost')) {
    const roomMatch = cleaned.match(/room\d+|P2PChannel-mutex-\d+/);
    if (roomMatch) cleaned = roomMatch[0];
    else {
      const channelMatch = cleaned.match(/[?&]channel=([^&]+)/);
      if (channelMatch) cleaned = decodeURIComponent(channelMatch[1]);
    }
  }
  cleaned = cleaned
    .replace(/^https?:\/\//, '')
    .replace(/^\/\//, '')
    .replace(/localhost:\d+/, '')
    .replace(/[?&]channel=/, '')
    .replace(/\?.+$/, '')
    .replace(/[^a-zA-Z0-9_-]/g, '');
  if (/^\d+$/.test(cleaned)) cleaned = 'room' + cleaned;
  return cleaned.substring(0, 64);
};

export default function AgoraVideoCall() {
  // Get URL parameters from router
  const { roomId, channel } = useParams();
  const [searchParams] = useSearchParams();
  
  const [isJoined, setIsJoined] = useState(false);
  const [channelName, setChannelName] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [remoteUsersState, setRemoteUsersState] = useState({});
  const [currentUid, setCurrentUid] = useState(() => String(Date.now()));
  const [deviceService] = useState(() => new DeviceService());
  const [devices, setDevices] = useState({ microphones: [], speakers: [], cameras: [] });
  const [callDuration, setCallDuration] = useState(0);
  const [showPanel, setShowPanel] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [patientId, setPatientId] = useState(null);
        const [mediaBusy, setMediaBusy] = useState({ audio: false, video: false });
  const [joining, setJoining] = useState(false);
  
  // Role and user info from URL
  const [userInfo, setUserInfo] = useState({
    isMentor: false,
    isStudent: true,
    name: '',
    role: 'student',
    appointmentId: null,
    bookingId: null,
    studentId: null,
    mentorId: null
  });
    const [meetingBootstrap, setMeetingBootstrap] = useState(null);
  const [providerTokenData, setProviderTokenData] = useState(null);
  const [roomClosed, setRoomClosed] = useState(false);
  const joinToken = searchParams.get('token');
  const isStableMeetingLink = Boolean(joinToken && roomId);
  const isWorkspaceEmbed = searchParams.get('embed') === 'workspace';
  const isPatientAppEmbed = ['patient-app', 'student-app'].includes(searchParams.get('embed'));
  const isDoctorAppEmbed = ['doctor-app', 'mentor-app'].includes(searchParams.get('embed'));
  const isEmbeddedCall = isWorkspaceEmbed || isPatientAppEmbed || isDoctorAppEmbed;
  const clientPlatform = searchParams.get('client_platform') || '';
  const isIosWebView = useMemo(() => {
    if (clientPlatform === 'ios-webview') return true;
    if (typeof navigator === 'undefined') return false;
    return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !/Safari/i.test(navigator.userAgent);
  }, [clientPlatform]);
  const providerRequestOptions = useMemo(
    () => (isEmbeddedCall && isIosWebView
      ? { avoidProvider: 'agora', clientPlatform: 'ios-webview' }
      : {}),
    [isEmbeddedCall, isIosWebView]
  );

  const agoraClient = useRef(null);
  const localTracks = useRef({ audioTrack: null, videoTrack: null });
  const timerRef = useRef(null);
  const autoJoinAttemptedRef = useRef(false);
  const joinInProgressRef = useRef(false);
  const leftEventSentRef = useRef(false);
  const nativeEndNotifiedRef = useRef(false);

  const notifyNativeShell = useCallback((type, payload = {}) => {
    if (!isEmbeddedCall || typeof window === 'undefined' || !window.ReactNativeWebView?.postMessage) return;
    window.ReactNativeWebView.postMessage(JSON.stringify({ type, ...payload }));
  }, [isEmbeddedCall]);

  const notifyNativeCallEnded = useCallback((reason) => {
    if (nativeEndNotifiedRef.current) return;
    nativeEndNotifiedRef.current = true;
    notifyNativeShell('call-ended', { reason });
  }, [notifyNativeShell]);

  // Initialize socket connection on mount
  useEffect(() => {
    // Initialize socket
    initSocket(setRemoteUsersState);
    
    return () => {
      disconnectSocket();
    };
  }, []);
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs ? hrs + ':' : ''}${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Call timer
  useEffect(() => {
    if (isJoined) {
      timerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      setCallDuration(0);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isJoined]);

  useEffect(() => {
    if (!isEmbeddedCall) return undefined;
    return onCallEnded(() => {
      notifyNativeCallEnded('socket_call_ended');
    });
  }, [isEmbeddedCall, notifyNativeCallEnded]);

  // Setup chat message listener
  useEffect(() => {
    if (isJoined) {
      const unsubscribe = onChatMessage((message) => {
        setChatMessages(prev => [...prev, {
          id: Date.now(),
          sender: message.senderId,
          senderName: message.senderName || 'User',
          text: message.text,
          timestamp: message.timestamp || new Date().toISOString()
        }]);
      });
      return unsubscribe;
    }
  }, [isJoined]);

  // Fetch patient data from URL params
  useEffect(() => {
    // Set loading to true
    setLoading(true);

    if (joinToken && roomId) {
      let cancelled = false;
      setRoomClosed(false);
      nativeEndNotifiedRef.current = false;

      const loadStableMeeting = async () => {
        try {
          const bootstrap = await bootstrapMeetingRoom(roomId, joinToken);
          if (cancelled) return;

          setMeetingBootstrap(bootstrap);
          const isMentorRole = ['mentor', 'doctor'].includes(bootstrap.user?.role) || bootstrap.user?.is_mentor;
          setUserInfo({
            isMentor: Boolean(isMentorRole),
            isStudent: !isMentorRole,
            name: bootstrap.user?.display_name || (isMentorRole ? 'Mentor' : 'Student'),
            role: isMentorRole ? 'mentor' : 'student',
            appointmentId: bootstrap.room?.appointment_id || null,
            bookingId: bootstrap.room?.booking_id || bootstrap.room?.appointment_id || null,
            studentId: bootstrap.room?.student_id || bootstrap.room?.patient_id || null,
            mentorId: bootstrap.room?.mentor_id || bootstrap.room?.doctor_id || null
          });
          setChannelName(bootstrap.provider?.channel || bootstrap.room?.provider_channel || bootstrap.room?.room_id || roomId);

          if (bootstrap.provider?.key && bootstrap.provider.key !== 'agora') {
            const providerConfig = await fetchProviderJoinConfig(roomId, joinToken, providerRequestOptions);
            if (!cancelled) {
              setProviderTokenData(providerConfig);
            }
          }
        } catch (err) {
          if (!cancelled) {
            if (err.code === 'ROOM_CLOSED' || err.code === 'ROOM_EXPIRED') {
              setRoomClosed(true);
            }
            setError(err.message || 'Unable to prepare this meeting');
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      };

      loadStableMeeting();
      return () => {
        cancelled = true;
      };
    }
    
    // Parse URL parameters from both router params and search params
    const channelParam = searchParams.get('channel') || channel || roomId || '';
    const roleParam = searchParams.get('role') || 'student';
    const nameParam = searchParams.get('name') || 'User';
    const appointmentIdParam = searchParams.get('appointmentId') || null;
    const patientIdParam = searchParams.get('patientId') || null;
    const doctorIdParam = searchParams.get('doctorId') || null;
    
    setUserInfo({
      isDoctor: roleParam === 'doctor',
      isPatient: roleParam === 'patient',
      isMentor: roleParam === 'mentor',
      isStudent: roleParam === 'student',
      name: decodeURIComponent(nameParam),
      role: roleParam,
      appointmentId: appointmentIdParam,
      bookingId: searchParams.get('bookingId') || appointmentIdParam,
      patientId: patientIdParam,
      doctorId: doctorIdParam
    });
    
    // Get patient ID from URL params or existing state
    const patient = patientIdParam || searchParams.get('patientId');
    if (patient) {
      setPatientId(patient);
      
    }
    
    // Get channel from URL if present
    if (channelParam) {
      setChannelName(channelParam);
    }
    
    // Mark loading complete
    setLoading(false);
  }, [roomId, channel, searchParams, joinToken, providerRequestOptions]);

  // Fetch patient data from API
  
  // Fetch Agora token
  const fetchAgoraToken = useCallback(async (channel, uid) => {
    if (isStableMeetingLink) {
      const tokenData = await fetchProviderJoinConfig(roomId, joinToken, providerRequestOptions);
      if (tokenData.provider !== 'agora') {
        setProviderTokenData(tokenData);
        // Signal caller to silently stop Agora join; ProviderFrameMeeting will render.
        const redirect = new Error('Non-Agora provider selected; rendering provider frame.');
        redirect.code = 'PROVIDER_REDIRECT';
        throw redirect;
      }
      setProviderTokenData(tokenData);
      return {
        token: tokenData.config.token,
        appId: tokenData.config.appId,
        channel: tokenData.config.channel || channel,
        uid: tokenData.config.uid
      };
    }

    const res = await fetch(`${SERVER_URL}/api/generate-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channelName: channel, uid: String(uid), role: 'publisher' })
    });
    const data = await res.json();
    if (!data.token || !data.appId) throw new Error('Invalid token');
    return { token: data.token, appId: data.appId, channel };
  }, [isStableMeetingLink, joinToken, providerRequestOptions, roomId]);

  const retryFramedProvider = useCallback(async () => {
    if (!roomId || !joinToken) return false;

    setError('');
    try {
      const tokenData = await fetchProviderJoinConfig(roomId, joinToken, providerRequestOptions);
      setProviderTokenData(tokenData);
      return true;
    } catch (err) {
      if (err.code === 'ROOM_CLOSED' || err.code === 'ROOM_EXPIRED') {
        setRoomClosed(true);
      }
      setError(err.message || 'Unable to reconnect to this secure meeting');
      return false;
    }
  }, [joinToken, providerRequestOptions, roomId]);

  // Fetch devices
  const fetchDevices = useCallback(async () => {
    const data = await deviceService.fetchDevices();
    setDevices(data);
  }, [deviceService]);

  const createLocalMediaTracks = useCallback(async () => {
    try {
      await fetchDevices();
    } catch (err) {
      console.warn('Unable to refresh media devices before joining:', err);
    }

    const selected = deviceService.getSelectedDevices();
    const audioOptions = selected.microphone ? { microphoneId: selected.microphone } : {};
    const videoOptions = selected.camera ? { cameraId: selected.camera } : {};

    try {
      return await AgoraRTC.createMicrophoneAndCameraTracks(audioOptions, videoOptions);
    } catch (combinedError) {
      console.warn('Creating camera and microphone together failed, trying tracks separately:', combinedError);
    }

    const createAudioTrack = async () => {
      const attempts = audioOptions.microphoneId ? [audioOptions, {}] : [{}];
      for (const options of attempts) {
        try {
          return await AgoraRTC.createMicrophoneAudioTrack(options);
        } catch (err) {
          console.warn('Microphone track attempt failed:', err);
        }
      }
      return null;
    };

    const createVideoTrack = async () => {
      const attempts = videoOptions.cameraId ? [videoOptions, {}] : [{}];
      for (const options of attempts) {
        try {
          return await AgoraRTC.createCameraVideoTrack(options);
        } catch (err) {
          console.warn('Camera track attempt failed:', err);
        }
      }
      return null;
    };

    const [audioTrack, videoTrack] = await Promise.all([
      createAudioTrack(),
      createVideoTrack()
    ]);

    if (!audioTrack && !videoTrack) {
      throw new Error('No camera or microphone was found. Connect a device, allow browser permission, then refresh and join again.');
    }

    return [audioTrack, videoTrack];
  }, [deviceService, fetchDevices]);

  // Switch devices
  const switchMicrophone = async (id) => { await deviceService.switchMicrophone(id, localTracks.current.audioTrack); fetchDevices(); };
  const switchSpeaker = async (id) => { await deviceService.switchSpeaker(id); setTimeout(() => deviceService.refreshSpeakerForAllTracks(), 500); fetchDevices(); };
  const switchCamera = async (id) => { await deviceService.switchCamera(id, localTracks.current.videoTrack); fetchDevices(); };

  // Join call
  const joinCallWithChannel = useCallback(async (channel) => {
    const final = cleanChannelName(channel);
    if (!final) {
      setError('Invalid meeting channel.');
      autoJoinAttemptedRef.current = false;
      return;
    }

    if (joinInProgressRef.current) return;
    joinInProgressRef.current = true;
    setJoining(true);

    const uid = currentUid || String(Date.now());
    if (!currentUid) setCurrentUid(uid);
    let joinChannel = final;
    let joinUid = uid;
    let joinedAgora = false;
    let joinedSocketRoom = false;

    try {
      const { token, appId, channel: providerChannel, uid: providerUid } = await fetchAgoraToken(final, uid);
      joinChannel = cleanChannelName(providerChannel || final);
      joinUid = isStableMeetingLink ? String(providerUid || '') : uid;
      if (!joinUid) throw new Error('Video provider did not issue a participant identity');
      if (joinUid !== currentUid) setCurrentUid(joinUid);
      if (!agoraClient.current) {
        agoraClient.current = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });

        // Retry playing a video track until the DOM element appears (handles slow re-renders on low-end devices).
        const playWhenReady = (elementId, videoTrack, attempts = 0) => {
          const el = document.getElementById(elementId);
          if (el) {
            videoTrack.play(el);
          } else if (attempts < 15) {
            setTimeout(() => playWhenReady(elementId, videoTrack, attempts + 1), 150);
          }
        };

        agoraClient.current.on('user-published', async (user, type) => {
          await agoraClient.current.subscribe(user, type);
          setRemoteUsersState(p => ({
            ...p,
            [user.uid]: {
              ...(p[user.uid] || {}),
              uid: String(user.uid),
              muted: type === 'audio' ? false : p[user.uid]?.muted || false,
              videoOff: type === 'video' ? false : p[user.uid]?.videoOff || false
            }
          }));
          if (type === 'audio' && user.audioTrack) {
            deviceService.addRemoteAudioTrack(user.audioTrack);
            user.audioTrack.play();
          }
          if (type === 'video' && user.videoTrack) {
            playWhenReady(`video-${user.uid}`, user.videoTrack);
          }
        });

        agoraClient.current.on('user-unpublished', (user, type) => {
          if (type === 'audio' && user.audioTrack) deviceService.removeRemoteAudioTrack(user.audioTrack);
          setRemoteUsersState(p => ({
            ...p,
            [user.uid]: {
              ...(p[user.uid] || {}),
              uid: String(user.uid),
              muted: type === 'audio' ? true : p[user.uid]?.muted || false,
              videoOff: type === 'video' ? true : p[user.uid]?.videoOff || false
            }
          }));
        });

        agoraClient.current.on('user-left', (user) => {
          deviceService.removeRemoteAudioTrack(user.audioTrack);
          setRemoteUsersState(p => { const n = { ...p }; delete n[user.uid]; return n; });
        });

        // Renew Agora token before it expires (tokens last 1 hour; renew at 10 min remaining).
        agoraClient.current.on('token-privilege-will-expire', async () => {
          try {
            const tokenData = await fetchAgoraToken(joinChannel, joinUid);
            await agoraClient.current.renewToken(tokenData.token);
          } catch (err) {
            console.error('[Agora] Token renewal failed:', err.message);
          }
        });

        // token-privilege-did-expire fires if the token already expired before renewal succeeded.
        agoraClient.current.on('token-privilege-did-expire', async () => {
          console.warn('[Agora] Token expired — fetching new token and rejoining');
          try {
            const tokenData = await fetchAgoraToken(joinChannel, joinUid);
            await agoraClient.current.renewToken(tokenData.token);
          } catch (err) {
            setError('Session token expired. Please leave and rejoin the call.');
          }
        });

        // Surface network state changes so the user sees a reconnecting indicator.
        agoraClient.current.on('connection-state-change', (curState, prevState, reason) => {
          if (curState === 'RECONNECTING') {
            setError('Network issue — reconnecting...');
          } else if (curState === 'CONNECTED') {
            setError('');
          } else if (curState === 'DISCONNECTED') {
            setError('Connection lost. Check your internet and use the rejoin button if the call does not recover.');
          }
        });
      }

      await agoraClient.current.join(appId, joinChannel, token, joinUid);
      joinedAgora = true;
      joinRoom(joinChannel, joinUid, userInfo.name || 'Participant');
      joinedSocketRoom = true;

      const [a, v] = await createLocalMediaTracks();

      localTracks.current = { audioTrack: a, videoTrack: v };
      setIsJoined(true);
      setChannelName(joinChannel);
      setError('');
      setIsMuted(!a);
      setIsVideoOff(!v);
      if (isStableMeetingLink) {
        leftEventSentRef.current = false;
        await sendMeetingEvent(roomId, joinToken, 'joined', {
          provider: 'agora',
          channel: joinChannel,
          uid: joinUid
        }).catch(() => {});
      }

      // Play local video with retry — the DOM element may not exist until React re-renders setIsJoined(true).
      if (v) {
        const playLocal = (attempts = 0) => {
          const el = document.getElementById('local-video');
          if (el) {
            v.play(el);
          } else if (attempts < 15) {
            setTimeout(() => playLocal(attempts + 1), 150);
          }
        };
        playLocal();
      }

      const tracksToPublish = [a, v].filter(Boolean);
      if (tracksToPublish.length > 0) {
        await agoraClient.current.publish(tracksToPublish);
      }
      await fetchDevices();

    } catch (err) {
      if (localTracks.current.audioTrack) localTracks.current.audioTrack.close();
      if (localTracks.current.videoTrack) localTracks.current.videoTrack.close();
      localTracks.current = { audioTrack: null, videoTrack: null };
      if (joinedSocketRoom) leaveRoom(joinChannel, joinUid);
      if (joinedAgora && agoraClient.current) {
        await agoraClient.current.leave().catch((leaveError) => {
          console.warn('Failed to leave Agora after join error:', leaveError);
        });
      }
      setIsJoined(false);
      autoJoinAttemptedRef.current = false;
      // PROVIDER_REDIRECT is not a real error — providerTokenData has been set and
      // the render will switch to ProviderFrameMeeting automatically.
      if (err.code !== 'PROVIDER_REDIRECT') {
        if (err.code === 'ROOM_CLOSED' || err.code === 'ROOM_EXPIRED') {
          setRoomClosed(true);
        }
        setError('Join failed: ' + err.message);
      }
    } finally {
      joinInProgressRef.current = false;
      setJoining(false);
    }
  }, [createLocalMediaTracks, currentUid, deviceService, fetchAgoraToken, fetchDevices, isStableMeetingLink, joinToken, roomId, userInfo.name]);

  const syncAgoraLeft = useCallback(async (payload = {}) => {
    if (!isStableMeetingLink || leftEventSentRef.current || !roomId || !joinToken) return;
    leftEventSentRef.current = true;
    await sendMeetingEvent(roomId, joinToken, 'left', payload).catch((err) => {
      console.error('Failed to sync Agora participant exit:', err);
    });
  }, [isStableMeetingLink, joinToken, roomId]);

  // Leave call
  const leaveCall = useCallback(async ({ skipCompletion = false } = {}) => {
    if (localTracks.current.audioTrack) localTracks.current.audioTrack.close();
    if (localTracks.current.videoTrack) localTracks.current.videoTrack.close();
    if (agoraClient.current) {
      await agoraClient.current.leave().catch((err) => {
        console.warn('Failed to leave Agora cleanly:', err);
      });
    }
    leaveRoom(channelName, currentUid);
    setIsJoined(false);
    setIsMuted(false);
    setIsVideoOff(false);
    setRemoteUsersState({});
    setError('');
    localTracks.current = { audioTrack: null, videoTrack: null };
    if (isEmbeddedCall) {
      notifyNativeCallEnded('participant_left');
    }

    if (isStableMeetingLink) {
      try {
        await syncAgoraLeft({ source: 'agora_controls' });
        if ((userInfo.isMentor) && !isEmbeddedCall && !skipCompletion) {
          await completeMeetingRoom(roomId, joinToken, {
            notes: 'Mentorzaa session completed'
          });
        }
      } catch (err) {
        console.error('Failed to sync meeting close:', err);
      }
      return;
    }
    
    
  }, [channelName, currentUid, isEmbeddedCall, isStableMeetingLink, joinToken, notifyNativeCallEnded, patientId, roomId, syncAgoraLeft, userInfo]);

  useEffect(() => () => {
    if (isJoined) {
      syncAgoraLeft({ source: 'hosted_meeting_unmount' });
    }
  }, [isJoined, syncAgoraLeft]);

  useEffect(() => {
    if (!isStableMeetingLink || !meetingBootstrap || roomClosed) return undefined;

    let active = true;
    let checking = false;
    const checkRoomStatus = async () => {
      if (checking) return; // Prevent concurrent polls if the backend is slow.
      checking = true;
      try {
        const status = await fetchMeetingRoomStatus(roomId, joinToken);
        if (active && status.can_join === false) {
          setRoomClosed(true);
          setError(status.close_reason || 'This video consultation has ended.');
        }
      } catch (err) {
        if (active && (err.code === 'ROOM_CLOSED' || err.code === 'ROOM_EXPIRED')) {
          setRoomClosed(true);
          setError(err.message);
        }
      } finally {
        checking = false;
      }
    };

    checkRoomStatus();
    const timer = setInterval(checkRoomStatus, 4000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [isStableMeetingLink, joinToken, meetingBootstrap, roomClosed, roomId]);

  useEffect(() => {
    if (roomClosed && isJoined) {
      leaveCall({ skipCompletion: true });
    }
  }, [isJoined, leaveCall, roomClosed]);

  useEffect(() => {
    if (!roomClosed) return;
    notifyNativeCallEnded('room_closed');
  }, [notifyNativeCallEnded, roomClosed]);

  // Toggle mute/video
  const toggleMute = async () => {
    if (mediaBusy.audio) return;
    if (!localTracks.current.audioTrack) {
      setError('No microphone is available for this call.');
      return;
    }
    const nextMuted = !isMuted;
    setMediaBusy(prev => ({ ...prev, audio: true }));
    try {
      await localTracks.current.audioTrack.setEnabled(!nextMuted);
      setIsMuted(nextMuted);
      setError('');
      toggleMuteSocket(nextMuted, channelName, currentUid);
    } catch (err) {
      setError(err.message || 'Microphone could not be changed.');
    } finally {
      setMediaBusy(prev => ({ ...prev, audio: false }));
    }
  };

  const toggleVideo = async () => {
    if (mediaBusy.video) return;
    if (!localTracks.current.videoTrack) {
      setError('No camera is available for this call.');
      return;
    }
    const nextVideoOff = !isVideoOff;
    setMediaBusy(prev => ({ ...prev, video: true }));
    try {
      await localTracks.current.videoTrack.setEnabled(!nextVideoOff);
      setIsVideoOff(nextVideoOff);
      setError('');
      toggleVideoSocket(nextVideoOff, channelName, currentUid);
    } catch (err) {
      setError(err.message || 'Camera could not be changed.');
    } finally {
      setMediaBusy(prev => ({ ...prev, video: false }));
    }
  };

  // Handle chat message
  const handleSendMessage = () => {
    if (chatInput.trim()) {
      const message = {
        channelName,
        message: chatInput,
        senderId: currentUid,
        senderName: userInfo.name || (userInfo.isMentor ? 'Mentor' : 'Student')
      };
      sendChatMessage(message);
      setChatMessages(prev => [...prev, {
        id: Date.now(),
        sender: currentUid,
        senderName: 'You',
        text: chatInput,
        timestamp: new Date().toISOString()
      }]);
      setChatInput('');
    }
  };

  // Handle join button
  const handleJoin = () => {
    const channelInput = document.getElementById('channel-input')?.value || channelName;
    if (channelInput) {
      joinCallWithChannel(channelInput);
    }
  };

  // Join channel from URL if present — no artificial delay needed; socket join is queued via pendingRoom.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const channelFromUrl = params.get('channel') || roomId || channel;
    const activeProviderForJoin = providerTokenData?.provider || meetingBootstrap?.provider?.key;
    if (isStableMeetingLink && activeProviderForJoin && activeProviderForJoin !== 'agora') return;
    if (isStableMeetingLink && !meetingBootstrap) return;

    if (channelFromUrl && !isJoined && !loading) {
      if (autoJoinAttemptedRef.current) return;
      autoJoinAttemptedRef.current = true;
      setChannelName(channelFromUrl);
      joinCallWithChannel(channelFromUrl);
    }
  }, [isJoined, loading, roomId, channel, isStableMeetingLink, meetingBootstrap, providerTokenData?.provider, joinCallWithChannel]);

  // Render Join Form
  if (loading) {
    return (
      <div className="app-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
          <div className="spinner" style={{ 
            width: '48px', 
            height: '48px', 
            border: '3px solid rgba(255,255,255,0.1)', 
            borderTopColor: '#0d9488', 
            borderRightColor: '#38bdf8', 
            borderRadius: '50%', 
            animation: 'spin 0.8s linear infinite'
          }}></div>
          <MentorzaLogo variant="stacked" height={48} />
          <p style={{ fontSize: '14px', fontWeight: '600', color: '#94a3b8', letterSpacing: '0.02em', marginTop: '6px' }}>Initializing Mentorza Session...</p>
        </div>
      </div>
    );
  }

  const activeProviderKey = providerTokenData?.provider || meetingBootstrap?.provider?.key;
  if (isStableMeetingLink && meetingBootstrap && activeProviderKey && activeProviderKey !== 'agora') {
    return (
      <ProviderFrameMeeting
        bootstrap={meetingBootstrap}
        embedded={isEmbeddedCall}
        joinToken={joinToken}
        mentorAppEmbed={isDoctorAppEmbed}
        onClose={() => notifyNativeCallEnded('participant_left')}
        onRetry={retryFramedProvider}
        prepareError={error}
        providerTokenData={providerTokenData}
        roomClosed={roomClosed}
        roomUuid={roomId}
        studentAppEmbed={isPatientAppEmbed}
      />
    );
  }

  if (!isJoined && isStableMeetingLink) {
    return (
      <div className={`app-container secure-join-state ${isEmbeddedCall ? 'embedded-call' : ''}`}>
        <div className="secure-join-card">
          <MentorzaLogo variant="stacked" height={52} />
          <h2>{roomClosed ? 'Session ended' : error ? 'Secure meeting needs attention' : 'Mentorzaa Meeting'}</h2>
          <p>
            {roomClosed
              ? error || 'This secure session is no longer open.'
              : error
              ? error
              : 'Your private session is ready. Join when you are comfortable.'}
          </p>
          {!roomClosed && (
            <button
              className="join-button"
              disabled={joining}
              onClick={() => joinCallWithChannel(channelName || roomId)}
              type="button"
            >
              <Phone />
              {joining ? 'Connecting...' : error ? 'Try again' : 'Join meeting'}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!isJoined) {
    return (
      <div className="app-container" style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="call-header">
          <div className="call-info">
            <div style={{ display: 'flex', alignItems: 'center' }}><MentorzaLogo variant="horizontal" height={26} /></div>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', background: 'radial-gradient(circle at 50% 30%, rgba(15, 118, 110, 0.12), #070d17 75%)' }}>
          <div className="join-form">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '1.5rem' }}>
              <MentorzaLogo variant="stacked" height={44} />
              <h2 className="join-title" style={{ margin: 0, marginTop: '4px', fontSize: '1.35rem' }}>Join Mentorship Session</h2>
            </div>
            
            <div className="form-group">
              <label className="form-label">Channel Name / Session ID</label>
              <input
                id="channel-input"
                type="text"
                className="form-input"
                placeholder="Enter session channel name"
                defaultValue={channelName}
                onChange={(e) => setChannelName(e.target.value)}
              />
            </div>

            {error && (
              <div className="error-message">{error}</div>
            )}

            <button className="join-button" disabled={joining} onClick={handleJoin}>
              <Phone size={18} /> {joining ? 'Connecting to Room...' : 'Join Session'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render Active Call
  const canShowClinicalPanel = false;
  const isClinicalPanelOpen = false;
  const securePatientLink = isStableMeetingLink
    ? null
    : `${window.location.origin}?channel=${channelName}`;
  const remoteParticipantLabel = userInfo.isMentor ? 'Student' : 'Mentor';

  return (
    <div className={`app-container ${isEmbeddedCall ? 'embedded-call' : ''} ${isWorkspaceEmbed ? 'workspace-embed' : ''} ${isPatientAppEmbed ? 'patient-app-embed student-app-embed' : ''} ${isDoctorAppEmbed ? 'doctor-app-embed mentor-app-embed' : ''}`}>
      {/* Header */}
      <div className="call-header">
        <div className="call-info">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MentorzaLogo variant="horizontal" height={26} />
            {userInfo.name && <span className="user-name">{userInfo.name}</span>}
          </div>
          <div className="call-meta">
            <span className="channel-badge">Channel: {channelName}</span>
            <span className="duration-badge">Duration: {formatTime(callDuration)}</span>
            <span className={`role-badge ${userInfo.role}`}>
              {userInfo.isMentor ? <GraduationCap size={12} /> : <User size={12} />}
              {userInfo.isMentor ? 'Mentor' : userInfo.isStudent ? 'Student' : userInfo.role}
            </span>
            <div className="operational-bridge" title="Mentorzaa secure meeting connection" style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px', padding: '2px 8px', backgroundColor: 'rgba(16, 185, 129, 0.1)', borderRadius: '99px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
               <Zap size={10} className="text-emerald-400" fill="currentColor" />
               <span style={{ fontSize: '9px', fontWeight: '900', color: '#34d399', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Secure</span>
            </div>
          </div>
        </div>
        
      </div>

      {/* Main Content */}
      <div className={`call-content ${isClinicalPanelOpen ? 'with-panel' : ''}`}>
        {/* Video Area */}
        <div className="video-area">
          {isPatientAppEmbed && userInfo.isStudent && (
            <div className="patient-call-strip">
              <span className="live-dot" />
              <span>Mentor video is primary. Your preview stays compact and controls remain below.</span>
            </div>
          )}
          {isDoctorAppEmbed && userInfo.isMentor && (
            <div className="patient-call-strip doctor-call-strip">
              <span className="live-dot" />
              <span>Student video is primary. Close the meeting to return to your Mentorzaa sessions.</span>
            </div>
          )}

          <div className="video-grid focused-layout">
            {/* Local Video */}
            <div className={`video-card local ${isVideoOff ? 'camera-off' : ''}`}>
              <div className="video-player" style={{ background: '#000' }}>
                <div id="local-video" className="agora-video-slot" />
                {isVideoOff && (
                  <div className="video-off-overlay">
                    <VideoOff size={18} />
                    Camera off
                  </div>
                )}
                {isMuted && <div className="media-pill muted-pill"><MicOff size={12} /> Muted</div>}
                <div className="video-label">You</div>
              </div>
            </div>

            {/* Remote Videos */}
            <div className="video-card remote">
              <div className="video-player-list">
                {Object.keys(remoteUsersState).length === 0 ? (
                  <div className="waiting-room">
                    <Users size={48} />
                    <p>{userInfo.isMentor ? 'Waiting for the student to join...' : 'Waiting for the mentor to join...'}</p>
                  </div>
                ) : (
                  Object.entries(remoteUsersState).map(([uid, remoteState]) => (
                    <div key={uid} className={`video-player ${remoteState.videoOff ? 'camera-off' : ''}`}>
                      <div id={`remote-video-${uid}`} className="agora-video-slot" />
                      {remoteState.videoOff && (
                        <div className="video-off-overlay">
                          <VideoOff size={18} />
                          Camera off
                        </div>
                      )}
                      {remoteState.muted && <div className="media-pill muted-pill"><MicOff size={12} /> Muted</div>}
                      <div className="video-label">{remoteParticipantLabel}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="controls-bar">
            <div className="control-buttons">
              <button 
                className={`control-btn ${isMuted ? 'muted' : ''} ${mediaBusy.audio ? 'busy' : ''}`}
                onClick={toggleMute}
                disabled={mediaBusy.audio}
                aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {mediaBusy.audio ? <span className="control-spinner" /> : isMuted ? <MicOff /> : <Mic />}
                <span className="control-label">{isMuted ? 'Unmute' : 'Mute'}</span>
              </button>
              
              <button 
                className={`control-btn ${isVideoOff ? 'off' : ''} ${mediaBusy.video ? 'busy' : ''}`}
                onClick={toggleVideo}
                disabled={mediaBusy.video}
                aria-label={isVideoOff ? 'Turn camera on' : 'Turn camera off'}
                title={isVideoOff ? 'Turn On Video' : 'Turn Off Video'}
              >
                {mediaBusy.video ? <span className="control-spinner" /> : isVideoOff ? <VideoOff /> : <Video />}
                <span className="control-label">{isVideoOff ? 'Camera' : 'Video'}</span>
              </button>

              <button 
                className={`control-btn chat-btn ${showChat ? 'active' : ''}`}
                onClick={() => setShowChat(!showChat)}
                aria-label={showChat ? 'Close chat' : 'Open chat'}
                title="Chat"
              >
                <MessageSquare />
                <span className="control-label">Chat</span>
              </button>

              <button 
                className="control-btn hangup"
                onClick={() => leaveCall()}
                aria-label="Leave video call"
                title="End Call"
              >
                <PhoneOff />
                <span className="hangup-text">Leave</span>
              </button>
            </div>

            {/* Device Controls */}
            <div className="device-controls">
              <select 
                value={deviceService.getSelectedDevices().microphone || ''} 
                onChange={e => switchMicrophone(e.target.value)}
              >
                {devices.microphones.map(d => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label || 'Microphone'}</option>
                ))}
              </select>
              <select 
                value={deviceService.getSelectedDevices().speaker || ''} 
                onChange={e => switchSpeaker(e.target.value)}
              >
                {devices.speakers.map(d => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label || 'Speaker'}</option>
                ))}
              </select>
              <select 
                value={deviceService.getSelectedDevices().camera || ''} 
                onChange={e => switchCamera(e.target.value)}
              >
                {devices.cameras.map(d => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label || 'Camera'}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Chat Panel */}
        {showChat && (
          <div className="chat-panel">
            <div className="chat-header">
              <h3>Chat</h3>
              <button className="close-btn" onClick={() => setShowChat(false)}>
                <X />
              </button>
            </div>
            <div className="chat-messages">
              {chatMessages.length === 0 ? (
                <p className="no-messages">No messages yet</p>
              ) : (
                chatMessages.map(msg => (
                  <div key={msg.id} className={`chat-message ${msg.sender === currentUid ? 'sent' : 'received'}`}>
                    <div className="message-sender">{msg.senderName}</div>
                    <div className="message-text">{msg.text}</div>
                    <div className="message-time">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="chat-input">
              <input
                type="text"
                placeholder="Type a message..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <button onClick={handleSendMessage}>
                <Send />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
